import { useEffect, useState } from "react";
import { supabase } from "@/lib/supabase";
import { useAuth } from "@/hooks/useAuth";

/**
 * Resolves the current admin user's "scope" for the My Teams area.
 *
 * - `district` — superintendent or district-level admin (sees a list of schools)
 * - `school`   — principal or school-level admin (sees their school dashboard)
 * - `super_admin` — global admin (defaults to district list across system)
 * - `none`     — no admin scope known yet (UI falls back to sample data)
 *
 * Source-of-truth is `profiles` + `user_roles` from AE-35 migrations. We don't
 * crash if those tables aren't reachable — we just degrade to `none` so the
 * sample UI still renders for product/design review.
 */
export type LeaderScope =
  | { kind: "district"; districtId: string }
  | { kind: "school"; schoolId: string }
  | { kind: "super_admin" }
  | { kind: "none" };

interface ProfileLite {
  district_id: string | null;
  school_id: string | null;
  is_super_admin: boolean | null;
}

interface RoleLite {
  role: string;
}

export function useLeaderScope(): { scope: LeaderScope; isLoading: boolean } {
  const { userData } = useAuth();
  const userId = userData?.user_id;

  const [scope, setScope] = useState<LeaderScope>({ kind: "none" });
  const [isLoading, setIsLoading] = useState<boolean>(Boolean(userId));

  useEffect(() => {
    let cancelled = false;
    if (!userId) {
      setScope({ kind: "none" });
      setIsLoading(false);
      return () => {
        cancelled = true;
      };
    }

    setIsLoading(true);

    void (async () => {
      try {
        const [profileResult, rolesResult] = await Promise.all([
          supabase
            .from("profiles" as never)
            .select("district_id, school_id, is_super_admin")
            .eq("id", userId)
            .maybeSingle(),
          supabase.from("user_roles" as never).select("role").eq("user_id", userId),
        ]);

        if (cancelled) return;

        const profile = (profileResult.data ?? null) as ProfileLite | null;
        const rolesRaw = (rolesResult.data ?? []) as RoleLite[];
        const roles = new Set(rolesRaw.map((r) => r.role));

        const isSuper = Boolean(profile?.is_super_admin) || roles.has("super_admin");
        const isSuperintendent = roles.has("superintendent");
        const isPrincipal = roles.has("principal");

        if (isSuper) {
          setScope({ kind: "super_admin" });
        } else if (isSuperintendent && profile?.district_id) {
          setScope({ kind: "district", districtId: profile.district_id });
        } else if (isPrincipal && profile?.school_id) {
          setScope({ kind: "school", schoolId: profile.school_id });
        } else {
          setScope({ kind: "none" });
        }
      } catch (err) {
        if (!cancelled) {
          console.warn("[useLeaderScope] falling back to none — admin tables not reachable", err);
          setScope({ kind: "none" });
        }
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [userId]);

  return { scope, isLoading };
}
